import React from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
  useTheme,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import StarIcon from '@mui/icons-material/Star';
import Footer from '../components/Footer';

// Styled components
const PricingCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'featured',
})(({ theme, featured }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  borderRadius: theme.shape.borderRadius * 2,
  position: 'relative',
  overflow: 'visible',
  border: featured ? `2px solid ${theme.palette.primary.main}` : `1px solid rgba(0, 0, 0, 0.12)`,
  backgroundColor: featured ? 'rgba(25, 118, 210, 0.04)' : 'white',
  [theme.breakpoints.up('md')]: {
    ...(featured && {
      boxShadow: '0 8px 24px rgba(25, 118, 210, 0.15)',
      transform: 'translateY(-8px)',
    }),
  },
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.up('md')]: {
      transform: featured ? 'translateY(-12px)' : 'translateY(-4px)',
    }
  }
}));

const PricingPage = () => {
  const theme = useTheme();

  // Define pricing tiers
  const tiers = [
    {
      title: 'Trial',
      price: '99',
      duration: '7 Days',
      description: 'Try QResto risk-free',
      features: [
        'Single QR code',
        'Up to 5 menu items',
        'Basic menu design',
        'Standard support',
      ],
      buttonText: 'Start Trial',
      featured: false,
      message: 'Hi! I would like to start the 7-day trial of QResto for ₹99. Please help me get started.',
    },
    {
      title: 'Monthly',
      price: '499',
      duration: 'per month',
      description: 'Perfect for growing restaurants',
      features: [
        'Unlimited QR codes',
        'Unlimited menu items',
        'Custom menu design',
        'Priority support',
        'Regular updates',
        'Analytics dashboard',
      ],
      buttonText: 'Get Started',
      featured: true,
      mostPopular: true,
      message: 'Hi! I am interested in the Monthly plan (₹499/month) for QResto. Please provide more details and help me get started.',
    },
    {
      title: 'Annually',
      price: '4,999',
      duration: 'per year',
      savings: 'Save ₹989',
      description: 'Best value for committed restaurants',
      features: [
        'Unlimited QR codes',
        'Unlimited menu items',
        'Custom menu design',
        'Priority support',
        'Regular updates',
        'Analytics dashboard',
        'Dedicated account manager',
      ],
      buttonText: 'Get Started',
      featured: false,
      message: 'Hi! I am interested in the Annual plan (₹4,999/year) for QResto. Please provide more details and help me get started.',
    }
  ];

  return (
    <>
      <Box
        sx={{
          pt: { xs: 8, sm: 12 },
          pb: { xs: 6, sm: 16 },
          px: 2,
          textAlign: 'center',
          background: 'linear-gradient(180deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            component="h1" 
            gutterBottom
            fontWeight="bold"
            sx={{ 
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
            }}
          >
            Simple, Transparent Pricing
          </Typography>
          <Typography 
            variant="h5" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 4, sm: 8 }, 
              maxWidth: "700px", 
              mx: "auto",
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            Choose the plan that's right for your restaurant and start engaging customers with digital menus
          </Typography>
          
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start" justifyContent="center">
            {tiers.map((tier) => (
              <Grid 
                item 
                key={tier.title} 
                xs={12} 
                sm={10}
                md={4}
                sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  ...(tier.featured && {
                    mt: { xs: 0, md: -3 },
                  })
                }}
              >
                <PricingCard featured={tier.featured} elevation={tier.featured ? 8 : 1}>
                  {tier.mostPopular && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: { xs: 12, md: -12 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        borderRadius: 20,
                        px: 2,
                        py: 0.5,
                        fontWeight: 'medium',
                        fontSize: '0.875rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 1,
                      }}
                    >
                      <StarIcon fontSize="small" />
                      Most Popular
                    </Box>
                  )}
                  <CardHeader
                    title={tier.title}
                    titleTypographyProps={{ align: 'center', fontWeight: 'bold', variant: 'h5' }}
                    subheaderTypographyProps={{ align: 'center', color: 'text.secondary' }}
                    subheader={tier.description}
                    sx={{
                      pt: { xs: tier.mostPopular ? 6 : 4, md: 4 },
                      pb: 2,
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, px: { xs: 2, sm: 3 }, pb: { xs: 3, sm: 4 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', mb: 2 }}>
                      <Typography 
                        variant="h4" 
                        color="text.primary" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                      >
                        ₹
                      </Typography>
                      <Typography 
                        variant="h2" 
                        color={tier.featured ? 'primary.main' : 'text.primary'} 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '2.5rem', sm: '3rem', md: '3.75rem' } }}
                      >
                        {tier.price}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 1 }}>
                      {tier.duration}
                    </Typography>
                    {tier.savings && (
                      <Typography variant="subtitle2" color="success.main" align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
                        {tier.savings}
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <List sx={{ py: 2 }} disablePadding>
                      {tier.features.map((feature) => (
                        <ListItem key={feature} disablePadding disableGutters sx={{ py: 1 }}>
                          <ListItemIcon sx={{ minWidth: 28, color: 'success.main' }}>
                            <CheckIcon />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{ 
                              color: 'text.primary',
                              fontWeight: 'medium',
                            }} 
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  <CardActions sx={{ p: { xs: 2, sm: 3 }, pt: 0 }}>
                    <Button 
                      component={RouterLink}
                      to={`/contact?plan=${encodeURIComponent(tier.title)}&message=${encodeURIComponent(tier.message)}`}
                      fullWidth 
                      variant={tier.featured ? "contained" : "outlined"}
                      color={tier.featured ? "primary" : "primary"}
                      size="large"
                      sx={{
                        py: { xs: 1.25, sm: 1.5 },
                        fontSize: { xs: '0.95rem', sm: '1rem' }
                      }}
                    >
                      {tier.buttonText}
                    </Button>
                  </CardActions>
                </PricingCard>
              </Grid>
            ))}
          </Grid>

          {/* Custom Plan Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={3}
              sx={{ 
                mt: { xs: 4, sm: 6 }, 
                p: { xs: 3, sm: 4 }, 
                borderRadius: 3,
                textAlign: 'center',
                background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
                border: '1px solid',
                borderColor: 'primary.main',
              }}
            >
              <Typography 
                variant="h4" 
                gutterBottom 
                fontWeight="bold"
                sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                Need a Custom Solution?
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary" 
                paragraph 
                sx={{ 
                  maxWidth: 600, 
                  mx: 'auto', 
                  mb: 3,
                  fontSize: { xs: '0.95rem', sm: '1rem' }
                }}
              >
                Have specific requirements or managing multiple locations? Contact us to discuss a custom plan tailored to your needs.
              </Typography>
              <Button 
                variant="contained" 
                component={RouterLink}
                to="/contact"
                size="large"
                sx={{ px: { xs: 3, sm: 4 } }}
              >
                Contact Us for Custom Pricing
              </Button>
            </Paper>
          </Grid>
        </Container>
      </Box>
      
      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ py: { xs: 6, sm: 8 } }}>
        <Typography 
          variant="h3" 
          component="h2" 
          align="center" 
          fontWeight="bold" 
          gutterBottom
          sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}
        >
          Frequently Asked Questions
        </Typography>
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 1, sm: 2 } }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Can I cancel anytime?
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                How long does it take to set up?
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                Setup is instant! Once you sign up, you can start creating your digital menu immediately.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                What if I need to update my menu?
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                You can update your menu anytime through your dashboard. Changes are reflected instantly on your QR code.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2, height: '100%', bgcolor: 'grey.50' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                Do customers need to download an app?
              </Typography>
              <Typography variant="body1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                No, your digital menu works directly in web browsers. Customers simply scan the QR code with their phone's camera.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: { xs: 4, sm: 6 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Still have questions?
          </Typography>
          <Button 
            variant="contained" 
            component={RouterLink}
            to="/contact"
            size="large"
            sx={{ mt: 2 }}
          >
            Contact Our Team
          </Button>
        </Box>
      </Container>

      <Footer />
    </>
  );
};

export default PricingPage;